import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { BarcodeIssuesQuery } from '@/api/handlers/resources/products/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { BarcodeIssuesPage } from '@/api/models';
import { getUser } from '@/utils/authSession';
import { ProductQueryKeys, sessionScopeOf } from './productQueryKeys';

/** The key's branch, after `barcodeIssueLists()` and the session. */
const BRANCH_INDEX = ProductQueryKeys.barcodeIssueLists().length + 1;

/**
 * One page of the saved barcodes a scanner cannot use (`GET
 * products/barcode-issues`, bos-api API-207), kept variants left out, each
 * item's `stock` counting `branchId` (`null`: the caller's own scope). The
 * page's `totalCount` is the Barcode issues tab's count.
 *
 * Keyed under the signed-in organisation and user as well as the branch and
 * page (review of #241, finding 7). Keeps the current page on screen while
 * the next page of the same branch loads (`isPlaceholderData` flags it), but
 * never another branch's page: those rows' stock and fixes belong to that
 * branch (finding 4). Unwraps the envelope (rulebook §10): a failed read is
 * the query's `error`, code intact (`BRANCH_NOT_ALLOWED`,
 * `BRANCH_NOT_FOUND`), and a refusal is not tried again.
 */
export const useBarcodeIssuesQuery = (
    { branchId = null, page, pageSize }: BarcodeIssuesQuery,
    enabled = true,
) => {
    const { apiClient } = useApi();
    const session = sessionScopeOf(getUser());

    return useQuery<BarcodeIssuesPage, ErrorResponse>({
        queryKey: ProductQueryKeys.barcodeIssues(
            session,
            branchId,
            page,
            pageSize,
        ),
        queryFn: async () => {
            const result = await apiClient.products.getBarcodeIssues({
                branchId,
                page,
                pageSize,
            });
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        enabled,
        staleTime: 60_000,
        placeholderData: (previous, previousQuery) =>
            previousQuery?.queryKey[BRANCH_INDEX] === branchId &&
            previousQuery.queryKey[BRANCH_INDEX - 1] === session
                ? previous
                : undefined,
        // A refusal answers the same every time: only a failure that may
        // pass is tried again.
        retry: (failureCount, error) =>
            !isRefusal(error) && failureCount < RETRIES,
    });
};

const RETRIES = 3;

const isRefusal = (error: ErrorResponse): boolean =>
    error.status >= 400 && error.status < 500;
